import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCategorias,
    Categoria
} from '../../../services/blogCategorias';
import AddBlogCategoria from '../../../components/BlogCategoria/AddBlogCategoria';
import EditBlogCategoria from '../../../components/BlogCategoria/EditBlogCategoria';
import DeleteBlogCategoria from '../../../components/BlogCategoria/DeleteBlogCategoria';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const BlogCategoriaPage: React.FC = () => {
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Estados para modales
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCategoria, setSelectedCategoria] = useState<Categoria | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('categoriablog', userLevelId); // Ajusta el nombre del modelo según tus permisos

    const loadCategorias = useCallback(async () => {
        if (!perm?.can_read) {
            setCategorias([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchCategorias();
            setCategorias(data);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadCategorias();
    }, [loadCategorias]);

    const totalPages = Math.ceil(categorias.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedCategorias = categorias.slice(startIndex, startIndex + itemsPerPage);

    const handleRefresh = () => {
        loadCategorias();
    };

    const handleAddClick = () => {
        setShowAddModal(true);
    };

    const handleEditClick = (categoria: Categoria) => {
        setSelectedCategoria(categoria);
        setShowEditModal(true);
    };

    const handleDeleteClick = (categoria: Categoria) => {
        setSelectedCategoria(categoria);
        setShowDeleteModal(true);
    };

    const handleModalSuccess = () => {
        loadCategorias();
    };

    if (perm === null || (loading && categorias.length === 0)) return <Loading text="Cargando categorías..." />;

    return <><LumaResourceWorkspace title="Categorías de blog" description="Consulta y gestiona la información de tu coworking." group="Contenido" rows={categorias} columns={[{key:'nombre',label:'Nombre'},{key:'descripcion',label:'Descripción'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{handleAddClick()}:undefined} onEdit={perm?.can_update?handleEditClick:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history=""/><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{showAddModal && (<AddBlogCategoria
                        isOpen={showAddModal}
                        onClose={() => setShowAddModal(false)}
                        onSuccess={handleModalSuccess}
                    />)}{showEditModal && selectedCategoria && (<EditBlogCategoria
                        isOpen={showEditModal}
                        categoria={selectedCategoria}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedCategoria(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />)}{showDeleteModal && selectedCategoria && (<DeleteBlogCategoria
                        isOpen={showDeleteModal}
                        categoria={selectedCategoria}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setSelectedCategoria(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />)}</>;
};

export default BlogCategoriaPage;
